import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { menuService } from '../utils/api'; // Assuming menuService handles note upserts
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching a note for a specific day
 * @param {number} dayId - The day ID to fetch note for
 * @param {object} options - Additional options for the query
 */
export const useNote = (dayId, options = {}) => {
  return useQuery({
    queryKey: ['note', dayId],
    queryFn: () => menuService.getNote(dayId),
    enabled: !!dayId,
    ...options,
  });
};

/**
 * Custom hook for creating/updating a note for a specific day.
 * @param {object} options - Options for the mutation hook.
 * @param {function} options.onSuccess - Callback function on success.
 * @param {function} options.onError - Callback function on error.
 */
export const useUpsertNote = (options = {}) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  return useMutation({
    // mutationFn expects an object like { dayId, text, menuId (optional) }
    mutationFn: ({ dayId, text }) => menuService.upsertNote(dayId, text),
    
    // Implement optimistic update
    onMutate: async ({ dayId, text, menuId }) => {
      // Cancel any outgoing refetches for this note
      await queryClient.cancelQueries(['note', dayId]);
      
      // If menuId is provided, also cancel menu queries
      if (menuId) {
        await queryClient.cancelQueries(['menu', menuId, currentLanguage]);
      }
      
      // Snapshot the previous note
      const previousNote = queryClient.getQueryData(['note', dayId]);
      
      // Optimistically update the note
      const optimisticNote = {
        day_id: dayId,
        text,
        updated_at: new Date().toISOString(),
        _isOptimistic: true // Flag to identify optimistic entries
      };
      
      queryClient.setQueryData(['note', dayId], optimisticNote);
      
      // If we have a menuId, also try to update the note in the menu cache
      if (menuId) {
        const menuData = queryClient.getQueryData(['menu', menuId, currentLanguage]);
        if (menuData) {
          // Create a copy of the menu data that we can modify
          const updatedMenuData = { ...menuData };
          
          // Find the day in the menu data and update its note
          if (updatedMenuData.days && Array.isArray(updatedMenuData.days)) {
            const dayIndex = updatedMenuData.days.findIndex(day => day.id === dayId);
            if (dayIndex !== -1) {
              updatedMenuData.days[dayIndex] = {
                ...updatedMenuData.days[dayIndex],
                note: text,
              };
              
              // Update the menu in the cache
              queryClient.setQueryData(['menu', menuId, currentLanguage], updatedMenuData);
            }
          }
        }
      }
      
      return { previousNote, dayId, menuId };
    },
    
    // When mutate is called:
    onSuccess: (data, variables, context) => {
      console.log('[useUpsertNote] Note upserted successfully:', data);
      
      // Invalidate the specific note query to get fresh data
      queryClient.invalidateQueries(['note', variables.dayId]);
      
      // If menuId is provided, invalidate that specific menu query
      if (variables.menuId) {
        console.log(`[useUpsertNote] Invalidating menu query: ['menu', ${variables.menuId}, ${variables.language || currentLanguage}]`);
        queryClient.invalidateQueries({
          queryKey: ['menu', variables.menuId, variables.language || currentLanguage]
        });
      } else {
        // If no menuId is provided, invalidate all menu queries
        console.log('[useUpsertNote] No menuId provided, invalidating all menu queries');
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'menu'
        });
      }
      
      // Call user-provided onSuccess callback if it exists
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    
    // If mutation fails, roll back optimistic updates
    onError: (error, variables, context) => {
      console.error('[useUpsertNote] Error upserting note:', error);
      
      // Revert the optimistic update for the note
      if (context) {
        queryClient.setQueryData(['note', context.dayId], context.previousNote);
        
        // If we also updated a menu cache, invalidate it to refetch fresh data
        if (context.menuId) {
          queryClient.invalidateQueries(['menu', context.menuId, currentLanguage]);
        }
      }
      
      // Call user-provided onError callback if it exists
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    
    ...options, // Spread any additional options provided by the caller
  });
}; 